// src/components/scheduler/GrowthScheduler.tsx
import React, { useState, useEffect } from 'react';
import { LeadCaptureForm } from './LeadCaptureForm';
import { CalendarScheduler } from './CalendarScheduler';
import { CalendarPreview } from './CalendarPreview';
import { ConfirmationScreen } from './ConfirmationScreen';
import { SchedulerProvider } from './SchedulerContext';
import { SchedulerStep } from './types';

interface GrowthSchedulerProps {
    apiBaseUrl?: string;
    onComplete?: (appointmentId: string) => void;
    className?: string;
}

export const GrowthScheduler: React.FC<GrowthSchedulerProps> = ({
    apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080',
    onComplete,
    className = ''
}) => {
    const [currentStep, setCurrentStep] = useState<SchedulerStep>('form');
    const [appointmentId, setAppointmentId] = useState<string | null>(null);
    const [formCompleted, setFormCompleted] = useState(false);

    const handleFormComplete = () => {
        setFormCompleted(true);
        setCurrentStep('calendar');
    };

    const handleBookingComplete = (id: string) => {
        setAppointmentId(id);
        setCurrentStep('confirmation');
        if (onComplete) {
            onComplete(id);
        }
    };

    return (
        <SchedulerProvider apiBaseUrl={apiBaseUrl}>
            <div className={`growth-scheduler ${className}`}>
                {currentStep === 'confirmation' && appointmentId ? (
                    <div className="bg-white rounded-lg shadow-lg overflow-hidden max-w-4xl mx-auto p-6 md:p-8">
                        <ConfirmationScreen appointmentId={appointmentId} />
                    </div>
                ) : currentStep === 'calendar' ? (
                    // Full width calendar view after form completion
                    <div className="bg-white rounded-lg shadow-lg overflow-hidden max-w-5xl mx-auto">
                        {/* Progress Header */}
                        <div className="bg-gray-100 px-6 py-4">
                            <div className="flex items-center justify-center space-x-8">
                                <div className="flex items-center">
                                    <span className="mr-2 text-gray-500">✓</span>
                                    <span className="text-sm font-medium text-gray-500">
                                        Fill out the form
                                    </span>
                                </div>

                                <div className="flex items-center">
                                    <span className="mr-2 text-[#212A37]">•</span>
                                    <span className="text-sm font-medium text-[#212A37]">
                                        Book your event
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Full width calendar */}
                        <div className="p-6 md:p-8">
                            <CalendarScheduler onComplete={handleBookingComplete} />
                        </div>
                    </div>
                ) : (
                    // Initial form view with side-by-side layout
                    <div className="bg-white rounded-lg shadow-lg overflow-hidden max-w-5xl mx-auto">
                        {/* Progress Header */}
                        <div className="bg-gray-100 px-6 py-4">
                            <div className="flex items-center justify-center space-x-8">
                                <div className="flex items-center">
                                    <span className="mr-2 text-[#212A37]">•</span>
                                    <span className="text-sm font-medium text-[#212A37]">
                                        Fill out the form
                                    </span>
                                </div>

                                <div className="flex items-center">
                                    <span className="mr-2 text-gray-400">•</span>
                                    <span className="text-sm font-medium text-gray-400">
                                        Book your event
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Main Content - Side by Side Layout */}
                        <div className="grid md:grid-cols-2 divide-x divide-gray-200">
                            {/* Left Side - Form */}
                            <div className="p-6 md:p-8">
                                <LeadCaptureForm onComplete={handleFormComplete} />
                            </div>

                            {/* Right Side - Calendar with floating message */}
                            <div className="p-6 md:p-8 bg-gray-50 relative">
                                {/* Floating message box */}
                                <div className="absolute inset-0 z-10 flex items-center justify-center p-6">
                                    <div className="bg-white rounded-lg p-6 shadow-lg max-w-sm">
                                        <p className="text-gray-600 text-center text-lg">
                                            Please fill out the form before<br />
                                            choosing your time slot.
                                        </p>
                                    </div>
                                </div>

                                {/* Grayed out calendar preview */}
                                <CalendarPreview />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </SchedulerProvider>
    );
};